import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import {
  useConfigure,
  useCurrentRefinements,
  useHits,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch'
import { captureMessage } from '@sentry/nextjs'

import useCategoryDataStructure from '../components/hooks/useCategoryDataStructure'
import { useAuth } from './useAuth'
import { TypesenseProduct } from '../utils/types/search'
import { QueryParam } from '../utils/constants'
import { getCountryAndLocaleStrings, getCurrencyCode } from '../utils/locales'
import { Product, ProductText } from '../utils/types/Product'
import { IAimeosResponse } from '../utils/types/aimeosApi'

interface TypesenseProducts {
  data: Product[]
  isLoading: boolean
  total: number
}

interface TypesenseFetchConfig {
  supplier?: string
}

type ContractPriceList = Record<string, { percent: string, volume: string, key: string }[]>

const useTypesenseProducts = (config?: TypesenseFetchConfig): TypesenseProducts => {
  const {
    supplier,
  } = config || {}
  const { user } = useAuth()
  const { locale: countryAndLangString, query: urlQuery } = useRouter()
  const { country, locale } = getCountryAndLocaleStrings(countryAndLangString)
  const currency = getCurrencyCode(country)

  const { parentNodes, subCategory } = useCategoryDataStructure()
  const { query, refine } = useSearchBox()
  const { results } = useHits<TypesenseProduct>()
  const { status, refresh } = useInstantSearch()
  const { items: alLRefinements } = useCurrentRefinements()
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const searchQuery = [urlQuery[QueryParam.search]].flat()[0]
  const levels = ['one', 'two', 'three']
  const level = parentNodes.length - 1
  const selectedCategoryLabel = parentNodes.slice(-1).map((category) => category.label)

  const {
    data,
    mutate,
  } = useSWR<IAimeosResponse<ContractPriceList>>(user ? '/api/v1/price' : null)

  useEffect(() => {
    // Refresh contract prices when user changes
    mutate()
  }, [user, mutate])

  useConfigure({
    index: 'products',
    disjunctiveFacets: [
      `categories.one.${locale}`,
      `categories.two.${locale}`,
      `categories.three.${locale}`,
    ],
    disjunctiveFacetsRefinements: {
      ...(selectedCategoryLabel && { [`categories.${levels[level]}.${locale}`]: selectedCategoryLabel }),
    },
    facetsRefinements: {
      'country-availability': [country.toUpperCase()],
      ...(supplier && { supplier: [supplier] }),
    },
  })

  useEffect(() => {
    if (searchQuery && searchQuery !== query) {
      refine(searchQuery)
    }
  }, [query, searchQuery, refine])

  // Instantsearch sometimes gets stuck on stalled
  // Try refreshing after 3s if it seems to have gotten stuck
  if (status === 'stalled' && !loadingTimeoutRef.current) {
    const refinements = alLRefinements.map((attribute) => (
      `${attribute.indexId}-${attribute.attribute}: ${attribute.refinements.map((refinement) => refinement.label).join('/')}`))
    const hits = results?.hits.map((hit) => hit.sku).slice(10).join(',')
    loadingTimeoutRef.current = setTimeout(() => {
      captureMessage(`Typesense state stuck - ${status}: ${hits} (${refinements})`)
      refresh()
      loadingTimeoutRef.current = null
    }, 20000)
  } else if (status !== 'stalled' && loadingTimeoutRef.current) {
    clearTimeout(loadingTimeoutRef.current)
    loadingTimeoutRef.current = null
  }

  return {
    data: results?.hits.map((hit) => ({
      id: hit.id,
      'product.id': hit.id,
      'product.code': hit.sku,
      'product.label': hit.texts.en.name,
      'product.type': hit['variant-skus']?.length ? 'select' : 'default',
      'product.url': '',
      'product.status': 1,
      'product.config': [],
      'product.scale': '1',
      'product.target': '',
      'product.rating': '0.00',
      'product.ratings': 0,
      'product.ctime': hit.ctime,
      'product.highlight': hit.highlight,
      'product.sales-unit': hit.salesUnit,
      'product.referenceUnit': hit.referenceUnit,
      'product/property': [
        {
          id: '',
          'product.property.id': '',
          'product.property.type': 'per-box',
          'product.property.value': String(hit.perBox),
          'product.property.languageid': null,
        },
        {
          id: '',
          'product.property.id': '',
          'product.property.type': 'per-carton',
          'product.property.value': String(hit.perCarton),
          'product.property.languageid': null,
        },
      ],
      attribute: [],
      catalog: subCategory.flatMap((category) => (
        category['catalog.label'] === hit.categories.one[locale]
          ? [category].concat(category.catalog?.flatMap((categoryTwo) => (
            categoryTwo['catalog.label'] === hit.categories.two[locale]
              ? [categoryTwo].concat(categoryTwo.catalog?.flatMap((categoryThree) => (
                categoryThree['catalog.label'] === hit.categories.three[locale] ? [categoryThree] : []
              )) || []) : []
          )) || []) : []
      )),
      media: [{
        id: '',
        'media.previews': { 1280: hit.image },
        'media.type': 'default',
        'media.languageid': null,
        'media.mimetype': '',
        'media.domain': 'product',
        'media.label': hit['texts.en.name'],
        'media.preview': hit.image,
        'media.url': hit.image,

      }],
      price: [
        {
          id: '',
          'price.type': 'default',
          'price.label': hit.texts.en.name,
          'price.currencyid': currency,
          'price.domain': 'product',
          'price.quantity': 1,
          'price.value': hit.price[currency],
          'price.rebate': '0',
          'price.costs': 0,
          'price.taxrate': 25.5,
        },
        ...(data?.data?.[hit.sku] ? data?.data?.[hit.sku].map((price) => ({
          id: '',
          'price.type': 'default',
          'price.label': `custom_discount/${price.key}`,
          'price.currencyid': currency,
          'price.domain': 'product',
          'price.quantity': Number(price.volume),
          'price.value': String(((Number(hit.price[currency]) * 0.75) * (1 - (Number(price.percent) / 100))) / 0.75),
          'price.rebate': '0',
          'price.costs': 0,
          'price.taxrate': 25.5,
        })) : []),
      ],
      stock: [],
      supplier: [{
        id: '',
        'supplier.id': '',
        'supplier.code': hit.supplier,
        'supplier.label': hit.supplier,
        'supplier.status': 1,
        'service.config': hit['delivery-details'],
        'service.price': hit['delivery-costs'],
        product: [],
      }],
      text: Object.entries(hit.texts).flatMap<ProductText>(([lang, texts]) => {
        if (typeof texts === 'string') {
          if (['brand', 'model'].includes(lang)) {
            return [{
              'text.id': '',
              id: '',
              'text.label': lang,
              'text.type': lang as ProductText['text.type'],
              'text.content': texts,
              'text.languageid': null,
              'text.domain': 'product',
            }]
          }
        }
        if (lang === locale) {
          return Object.entries(texts).map(([type, content]) => ({
            'text.id': '',
            id: '',
            'text.label': type,
            'text.type': type as ProductText['text.type'],
            'text.content': content,
            'text.languageid': lang,
            'text.domain': 'product',
          }))
        }
        return []
      }),
      product: hit['variant-skus']?.map((variantSku, index) => ({
        id: '',
        'product.id': '',
        'product.code': variantSku,
        'product.label': hit.variance?.[index] || `Variant ${variantSku}`,
        'product.type': 'default',
        'product.url': '',
        'product.status': 1,
        'product.config': [],
        'product.scale': '1',
        'product.target': '',
        'product.rating': '0.00',
        'product.ratings': 0,
        attribute: [
          ...(hit.variance?.[index] ? [{
            id: '',
            'attribute.id': '',
            'attribute.languageid': null,
            'attribute.type': 'size',
            'attribute.code': hit.variance?.[index],
            'attribute.label': hit.variance?.[index],
            'attribute.domain': 'product',
            'product.lists.type': 'variant',
          }] : []),
        ],
        'product/property': [],
        catalog: [],
        text: [],
        media: [],
        price: [],
        supplier: [],
        stock: [],
      })) || [],
    })) || [],
    total: results?.nbHits || 0,
    isLoading: typeof results === 'undefined' || status === 'stalled',
  }
}

export default useTypesenseProducts
